import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { 
  getInvitationProjects, 
  createInvitationProject, 
  updateInvitationProject,
  deleteInvitationProject 
} from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { 
  Plus, 
  Edit, 
  Trash2,
  Calendar,
  MapPin,
  Users,
  Eye,
  Mail,
  Loader2,
  Heart,
  Search
} from 'lucide-react';
import { toast } from 'sonner';

const STATUS_MAP = { 0: 'Active', 1: 'Finish', 2: 'Pending' };
const STATUS_STYLES = {
  0: 'bg-green-50 text-green-700 border border-green-200',
  1: 'bg-blue-50 text-blue-700 border border-blue-200',
  2: 'bg-amber-50 text-amber-700 border border-amber-200'
};

export const InvitationsPage = () => {
  const navigate = useNavigate();
  const { isLeader } = useAuth();
  
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  
  // Modal states
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    bride_groom_name: '',
    client_email: '',
    sangjit_date: '',
    sangjit_place: '',
    target_pax: '',
    status: 0
  });
  
  // Delete confirmation
  const [deleteId, setDeleteId] = useState(null);

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      const response = await getInvitationProjects();
      setProjects(response.data);
    } catch (error) {
      toast.error('Failed to fetch invitation projects');
    } finally {
      setLoading(false);
    }
  };

  const openModal = (project = null) => {
    if (project) {
      setEditing(project);
      setForm({
        bride_groom_name: project.bride_groom_name,
        client_email: project.client_email,
        sangjit_date: project.sangjit_date,
        sangjit_place: project.sangjit_place,
        target_pax: project.target_pax.toString(),
        status: project.status !== undefined ? project.status : 0
      });
    } else {
      setEditing(null);
      setForm({
        bride_groom_name: '',
        client_email: '',
        sangjit_date: '',
        sangjit_place: '',
        target_pax: '',
        status: 0
      });
    }
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!form.bride_groom_name || !form.client_email || !form.sangjit_date || 
        !form.sangjit_place || !form.target_pax) {
      toast.error('Please fill all required fields');
      return;
    }

    try {
      const data = {
        ...form,
        target_pax: parseInt(form.target_pax),
        status: parseInt(form.status)
      };

      if (editing) {
        await updateInvitationProject(editing.id, data);
        toast.success('Invitation project updated');
      } else {
        await createInvitationProject(data);
        toast.success('Invitation project created');
      }
      setShowModal(false);
      fetchProjects();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save project');
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await deleteInvitationProject(deleteId);
      toast.success('Invitation project deleted');
      fetchProjects();
    } catch (error) {
      toast.error('Failed to delete project');
    } finally {
      setDeleteId(null);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('id-ID', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const filteredProjects = projects.filter(p => {
    const matchesSearch = p.bride_groom_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.client_email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.sangjit_place.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || p.status === parseInt(statusFilter);
    return matchesSearch && matchesStatus;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="w-8 h-8 animate-spin text-imperial" />
      </div>
    );
  }

  return (
    <div data-testid="invitations-page" className="space-y-8 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold text-imperial">Invitation Projects</h1>
          <p className="font-body text-muted-foreground mt-1">
            {isLeader() ? 'Manage all sangjit invitations and RSVPs' : 'View your invitation projects'}
          </p>
        </div>
        {isLeader() && (
          <Button 
            onClick={() => openModal()}
            className="bg-imperial hover:bg-imperial/90"
            data-testid="create-invitation-button"
          >
            <Plus className="w-4 h-4 mr-2" />
            New Invitation
          </Button>
        )}
      </div>

      {/* Search & Filter */}
      <div className="flex flex-col md:flex-row gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
          <Input
            placeholder="Search invitations..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 h-12 bg-white border-warm-grey"
            data-testid="search-invitations"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="h-12 w-40 bg-white border-warm-grey" data-testid="invitation-status-filter">
            <SelectValue placeholder="Filter status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="0">Active</SelectItem>
            <SelectItem value="1">Finish</SelectItem>
            <SelectItem value="2">Pending</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Projects Grid */}
      {filteredProjects.length === 0 ? (
        <Card className="card-luxury">
          <CardContent className="p-12 text-center">
            <Heart className="w-16 h-16 mx-auto text-muted-foreground/50 mb-4" />
            <p className="font-body text-muted-foreground">
              {searchTerm ? 'No invitations match your search' : 'No invitation projects yet'}
            </p>
            {isLeader() && !searchTerm && (
              <Button 
                onClick={() => openModal()}
                className="mt-4 bg-imperial hover:bg-imperial/90"
              >
                Create First Invitation
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredProjects.map((project) => (
            <Card 
              key={project.id} 
              className="card-luxury"
              data-testid={`invitation-${project.id}`}
            >
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between">
                  <CardTitle className="font-heading text-lg text-foreground flex items-center gap-2">
                    <Heart className="w-5 h-5 text-imperial" />
                    {project.bride_groom_name}
                  </CardTitle>
                  <span className={`px-2 py-1 text-xs font-medium rounded-full ${STATUS_STYLES[project.status] || STATUS_STYLES[0]}`}>
                    {STATUS_MAP[project.status] || 'Active'}
                  </span>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Mail className="w-4 h-4" />
                  <span className="font-body text-sm truncate">{project.client_email}</span>
                </div>
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Calendar className="w-4 h-4" />
                  <span className="font-body text-sm">{formatDate(project.sangjit_date)}</span>
                </div>
                <div className="flex items-center gap-2 text-muted-foreground">
                  <MapPin className="w-4 h-4" />
                  <span className="font-body text-sm truncate">{project.sangjit_place}</span>
                </div>
                
                <div className="pt-4 border-t border-warm-grey">
                  <div className="grid grid-cols-3 gap-2 text-center">
                    <div>
                      <p className="text-xs uppercase tracking-widest text-muted-foreground">Target</p>
                      <p className="font-heading text-lg font-bold">{project.target_pax}</p>
                    </div>
                    <div>
                      <p className="text-xs uppercase tracking-widest text-muted-foreground">RSVPs</p>
                      <p className="font-heading text-lg font-bold text-imperial">{project.total_rsvp}</p>
                    </div>
                    <div>
                      <p className="text-xs uppercase tracking-widest text-muted-foreground">Attending</p>
                      <p className="font-heading text-lg font-bold text-green-600">{project.total_attending}</p>
                    </div>
                  </div>
                </div>

                <div className="flex gap-2 pt-2">
                  <Button 
                    variant="outline"
                    className="flex-1 border-imperial text-imperial hover:bg-imperial hover:text-white"
                    onClick={() => navigate(`/invitations/${project.id}`)}
                    data-testid={`view-invitation-${project.id}`}
                  >
                    <Eye className="w-4 h-4 mr-2" />
                    View RSVPs
                  </Button>
                  {isLeader() && (
                    <>
                      <Button 
                        variant="outline"
                        size="icon"
                        onClick={() => openModal(project)}
                        data-testid={`edit-invitation-${project.id}`}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button 
                        variant="outline"
                        size="icon"
                        className="text-destructive hover:bg-destructive hover:text-white"
                        onClick={() => setDeleteId(project.id)}
                        data-testid={`delete-invitation-${project.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create/Edit Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">
              {editing ? 'Edit Invitation Project' : 'Create Invitation Project'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
                Bride & Groom Name *
              </Label>
              <Input
                value={form.bride_groom_name}
                onChange={(e) => setForm({ ...form, bride_groom_name: e.target.value })}
                placeholder="e.g., John & Jane"
                data-testid="bride-groom-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
                Client Email *
              </Label>
              <Input
                type="email"
                value={form.client_email}
                onChange={(e) => setForm({ ...form, client_email: e.target.value })}
                placeholder="client@email.com"
                data-testid="client-email-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
                Sangjit Date *
              </Label>
              <Input
                type="date"
                value={form.sangjit_date}
                onChange={(e) => setForm({ ...form, sangjit_date: e.target.value })}
                data-testid="sangjit-date-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
                Sangjit Place *
              </Label>
              <Input
                value={form.sangjit_place}
                onChange={(e) => setForm({ ...form, sangjit_place: e.target.value })}
                placeholder="e.g., Grand Ballroom, Jakarta"
                data-testid="sangjit-place-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
                Target Pax *
              </Label>
              <Input
                type="number"
                value={form.target_pax}
                onChange={(e) => setForm({ ...form, target_pax: e.target.value })}
                placeholder="e.g., 100"
                data-testid="target-pax-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
                Status
              </Label>
              <Select 
                value={form.status.toString()} 
                onValueChange={(value) => setForm({ ...form, status: parseInt(value) })}
              >
                <SelectTrigger data-testid="invitation-status-select">
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="0">Active</SelectItem>
                  <SelectItem value="1">Finish</SelectItem>
                  <SelectItem value="2">Pending</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowModal(false)}>Cancel</Button>
            <Button 
              onClick={handleSave} 
              className="bg-imperial hover:bg-imperial/90"
              data-testid="save-invitation-button"
            >
              {editing ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading">Delete Invitation Project?</AlertDialogTitle>
            <AlertDialogDescription className="font-body">
              This will permanently delete the project and all associated RSVPs. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="font-body">Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDelete}
              className="bg-destructive hover:bg-destructive/90"
              data-testid="confirm-delete-invitation"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default InvitationsPage;
